import api, { fetchAllPages, isPaginatedResponse } from './api';
import {
  AppNotification,
  EmailQueueEntry,
  TenantFeatureFlag,
  FeatureCode,
  EffectiveFeaturesResponse,
} from '../types';

// ─── Filter types ─────────────────────────────────────────────

interface NotificationFilters {
  category?: string;
  is_read?: boolean;
}

interface EmailFilters {
  status?: string;
}

export interface NotificationPage {
  count: number;
  next: string | null;
  previous: string | null;
  results: AppNotification[];
}

function normalizeNotificationPage(resp: unknown): NotificationPage {
  if (Array.isArray(resp)) {
    return {
      count: resp.length,
      next: null,
      previous: null,
      results: resp as AppNotification[],
    };
  }
  if (isPaginatedResponse<AppNotification>(resp)) {
    return resp;
  }
  return {
    count: 0,
    next: null,
    previous: null,
    results: [],
  };
}

// ─── Service ──────────────────────────────────────────────────

const notificationService = {
  // ── Notifications ───────────────────────────────────────────

  getNotifications: async (filters?: NotificationFilters): Promise<AppNotification[]> => {
    const params = new URLSearchParams();
    if (filters?.category) params.append('category', filters.category);
    if (filters?.is_read !== undefined) params.append('is_read', String(filters.is_read));
    const qs = params.toString();
    const basePath = `/notifications/${qs ? `?${qs}` : ''}`;
    return fetchAllPages<AppNotification>((page) => {
      if (page <= 1) return api.get(basePath);
      const sep = basePath.includes('?') ? '&' : '?';
      return api.get(`${basePath}${sep}page=${page}`);
    });
  },

  getNotificationsPage: async (
    filters?: NotificationFilters & { page?: number; page_size?: number },
  ): Promise<NotificationPage> => {
    const params = new URLSearchParams();
    if (filters?.category) params.append('category', filters.category);
    if (filters?.is_read !== undefined) params.append('is_read', String(filters.is_read));
    if (filters?.page) params.append('page', String(filters.page));
    if (filters?.page_size) params.append('page_size', String(filters.page_size));
    const qs = params.toString();
    const response = await api.get(`/notifications/${qs ? `?${qs}` : ''}`);
    return normalizeNotificationPage(response);
  },

  getNotification: async (id: string): Promise<AppNotification> => {
    return api.get(`/notifications/${id}/`);
  },

  getUnreadCount: async (): Promise<{ unread_count: number }> => {
    return api.get('/notifications/unread-count/');
  },

  markRead: async (id: string): Promise<AppNotification> => {
    return api.post(`/notifications/${id}/read/`, {});
  },

  markAllRead: async (): Promise<{ marked_read: number }> => {
    return api.post('/notifications/mark-all-read/', {});
  },

  // ── Email Queue (admin) ─────────────────────────────────────

  getEmails: async (filters?: EmailFilters): Promise<EmailQueueEntry[]> => {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    const qs = params.toString();
    const basePath = `/notifications/emails/${qs ? `?${qs}` : ''}`;
    return fetchAllPages<EmailQueueEntry>((page) => {
      if (page <= 1) return api.get(basePath);
      const sep = basePath.includes('?') ? '&' : '?';
      return api.get(`${basePath}${sep}page=${page}`);
    });
  },

  // ── Feature Flags ───────────────────────────────────────────

  getFeatureFlags: async (): Promise<TenantFeatureFlag[]> => {
    return api.get('/tenants/features/');
  },

  getEffectiveFeatures: async (): Promise<EffectiveFeaturesResponse> => {
    return api.get('/tenants/features/effective/');
  },

  toggleFeature: async (feature: FeatureCode, is_enabled: boolean): Promise<TenantFeatureFlag> => {
    return api.post('/tenants/features/toggle/', { feature, is_enabled });
  },

  updateFeatureConfig: async (feature: FeatureCode, config: Record<string, any>): Promise<TenantFeatureFlag> => {
    return api.put('/tenants/features/config/', { feature, config });
  },
};

export default notificationService;
